/**
 * تبدیل زمان ↔ مختصات پیوسته
 * =============================
 * چرا این فایل لازم شد — و چرا باگِ «قلم آزاد درست کار نمی‌کند» از همین‌جا
 * می‌آمد:
 *
 * `coordinateToTime` در کتابخانه نمودار، مختصات را به **زمانِ نزدیک‌ترین
 * کندل** گرد می‌کند. یعنی هر جای نمودار که کلیک کنید، نقطه‌تان می‌پرد روی
 * ستون همان کندل. برای یک خط روند بین دو کندل شاید به چشم نیاید، ولی برای
 * قلم آزاد فاجعه است: سیصد نقطه‌ای که با انگشت کشیده‌اید روی ده‌بیست ستون
 * جمع می‌شود و به‌جای یک خط نرم، یک نردبان پله‌ای می‌گیرید.
 *
 * ⚠ `coordinateToLogical` هم چاره نیست: آن هم همان عدد را گرد می‌کند
 * (نسخه ۴ کتابخانه `Math.ceil` روی نمایه اعشاری می‌زند). تنها تابعِ
 * واقعاً پیوسته `logicalToCoordinate` است.
 *
 * راه‌حل: `logicalToCoordinate` را روی دو نقطه صفر و یک صدا می‌زنیم و از
 * روی همان‌ها هم رفت و هم برگشت را خودمان حساب می‌کنیم، و بین زمانِ دو
 * کندل درون‌یابی می‌کنیم. این‌طور هم مکان‌نما دقیق است، هم ترسیم همچنان بر
 * حسب **زمان** ذخیره می‌شود (که با عوض‌شدن بازه زمانی سر جایش می‌ماند).
 *
 * بدون هیچ وابستگی — تا مستقیم آزمون‌پذیر باشد.
 */

/** فاصله میانگین دو کندل بر حسب ثانیه */
export function barStep(times: number[]): number {
  if (times.length < 2) return 0
  return (times[times.length - 1] - times[0]) / (times.length - 1)
}

/**
 * شماره منطقیِ اعشاری ← زمان.
 *
 * بیرون از محدوده داده هم جواب می‌دهد (با فاصله میانگین برون‌یابی می‌شود)،
 * چون کاربر حق دارد در فضای خالیِ سمت راست نمودار هم خط بکشد.
 */
export function timeFromLogical(times: number[], logical: number, step = barStep(times)): number | null {
  const n = times.length
  if (!n || !Number.isFinite(logical)) return null
  if (n === 1 || !step) return times[0]
  if (logical <= 0) return times[0] + logical * step
  if (logical >= n - 1) return times[n - 1] + (logical - (n - 1)) * step
  const i = Math.floor(logical)
  const f = logical - i
  const a = times[i]
  const b = times[i + 1]
  return a + f * (b - a)
}

/** زمان ← شماره منطقیِ اعشاری (وارونِ تابع بالا) */
export function logicalFromTime(times: number[], t: number, step = barStep(times)): number | null {
  const n = times.length
  if (!n || !Number.isFinite(t)) return null
  if (n === 1 || !step) return 0
  if (t <= times[0]) return (t - times[0]) / step
  if (t >= times[n - 1]) return (n - 1) + (t - times[n - 1]) / step

  // جست‌وجوی دودویی روی زمان‌های صعودی
  let lo = 0
  let hi = n - 1
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1
    if (times[m] <= t) lo = m
    else hi = m
  }
  const a = times[lo]
  const b = times[lo + 1]
  return b > a ? lo + (t - a) / (b - a) : lo
}

/** نمایه نزدیک‌ترین کندل به یک زمان */
export function nearestBarIndex(times: number[], t: number): number {
  const n = times.length
  if (!n) return -1
  let lo = 0
  let hi = n - 1
  while (lo < hi) {
    const m = (lo + hi) >> 1
    if (times[m] < t) lo = m + 1
    else hi = m
  }
  if (lo > 0 && Math.abs(times[lo - 1] - t) < Math.abs(times[lo] - t)) lo -= 1
  return lo
}

/**
 * ساده‌سازی خطِ دست‌آزاد (رامر–داگلاس–پویکر) — خروجی: **نمایه** نقطه‌هایی که
 * باید بمانند.
 *
 * چرا نمایه و نه خودِ نقطه‌ها: نقطه‌های اصلی بر حسب زمان و قیمت‌اند، ولی
 * «چقدر انحراف قابل چشم‌پوشی است» پرسشی تصویری است و باید در فضای پیکسل
 * سنجیده شود. با برگرداندن نمایه، هر دو نمایش کنار هم می‌مانند بدون اینکه
 * لازم باشد داده‌ی اصلی داخل ساختار پیکسلی جاسازی شود.
 */
export function simplifyIndices(pts: { x: number; y: number }[], tol = 1.2): number[] {
  const n = pts.length
  if (n < 3) return pts.map((_, i) => i)
  const keep = new Array(n).fill(false)
  keep[0] = true
  keep[n - 1] = true

  const stack: [number, number][] = [[0, n - 1]]
  while (stack.length) {
    const [s, e] = stack.pop()!
    let worst = 0
    let idx = -1
    for (let i = s + 1; i < e; i++) {
      const d = segDistance(pts[i], pts[s], pts[e])
      if (d > worst) { worst = d; idx = i }
    }
    if (idx > 0 && worst > tol) {
      keep[idx] = true
      stack.push([s, idx], [idx, e])
    }
  }
  const out: number[] = []
  for (let i = 0; i < n; i++) if (keep[i]) out.push(i)
  return out
}

/** همان بالا، ولی خودِ نقطه‌ها را برمی‌گرداند */
export function simplify<T extends { x: number; y: number }>(pts: T[], tol = 1.2): T[] {
  const keep = new Set(simplifyIndices(pts, tol))
  return pts.filter((_, i) => keep.has(i))
}

function segDistance(p: { x: number; y: number }, a: { x: number; y: number },
                     b: { x: number; y: number }): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y)
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}
